import type {
  OblivionMineAction,
  OblivionMineState,
  OblivionMineViewerState,
} from "@cloudnest/oblivion-mine-engine";
import {
  filterStateForViewer,
  validateAndReduceGameState,
} from "@cloudnest/oblivion-mine-engine";
import { oblivionMineEventToAction } from "./event-adapter";
import type { OblivionMineReplayFixtureEvent } from "./replay-fixtures.generated";

/**
 * UI-free 망각의 지뢰 (oblivion-mine) replay simulation. `init` seeds canonical
 * state; every other event is reconstructed via the event adapter and folded
 * through the engine reducer. Fails loudly on unsupported/rejected events.
 *
 * Hidden information: each snapshot carries the canonical `state` (for the
 * post-game recap toggle) and an observer `viewer` projection where both
 * players' armed mines are omitted. The replay screen renders `viewer` by
 * default so an un-triggered mine never reaches the DOM during step-through.
 */
export interface OblivionMineReplaySnapshot {
  readonly index: number;
  readonly event: OblivionMineReplayFixtureEvent;
  readonly action: OblivionMineAction | null;
  readonly state: OblivionMineState;
  readonly viewer: OblivionMineViewerState;
}

function requireState(
  state: OblivionMineState | null,
  eventType: string,
): OblivionMineState {
  if (state === null) throw new Error(`${eventType} before init`);
  return state;
}

function readInitState(payload: Record<string, unknown>): OblivionMineState {
  const candidate = (payload as { state?: unknown }).state;
  if (typeof candidate !== "object" || candidate === null) {
    throw new Error("oblivion-mine init event is missing a state snapshot");
  }
  return candidate as OblivionMineState;
}

function snapshot(
  index: number,
  event: OblivionMineReplayFixtureEvent,
  action: OblivionMineAction | null,
  state: OblivionMineState,
): OblivionMineReplaySnapshot {
  return {
    index,
    event,
    action,
    state,
    // Observer projection: both sides' armed mines hidden until triggered.
    viewer: filterStateForViewer(state, null),
  };
}

export function buildOblivionMineReplaySnapshots(
  events: readonly OblivionMineReplayFixtureEvent[],
): readonly OblivionMineReplaySnapshot[] {
  const snapshots: OblivionMineReplaySnapshot[] = [];
  let state: OblivionMineState | null = null;

  for (const event of events) {
    if (event.eventType === "init") {
      state = readInitState(event.payload);
      snapshots.push(snapshot(snapshots.length, event, null, state));
      continue;
    }

    const current = requireState(state, event.eventType);
    const action = oblivionMineEventToAction(event.eventType, event.payload);
    const result = validateAndReduceGameState(current, action);
    if (!result.accepted) {
      throw new Error(
        `${event.eventType} (#${event.sequenceNumber}) rejected: ${
          result.error ?? "Validation failed"
        }`,
      );
    }
    state = result.state;
    snapshots.push(snapshot(snapshots.length, event, action, state));
  }

  if (state === null) {
    throw new Error("Cannot replay an empty oblivion-mine event log");
  }
  return snapshots;
}
